/**
 * Meta Pixel — base loader + event tracking for start.jemloc.com.
 *
 * Mirrors the landing-factory's MetaPixel pattern so that the same Pixel ID
 * (set via env) attributes Lead clicks on the landings to CompleteRegistration
 * events here. No-ops if REACT_APP_META_PIXEL_ID is unset.
 */

type FbqFn = (...args: unknown[]) => void;

declare global {
    interface Window {
        fbq?: FbqFn;
        _fbq?: unknown;
    }
}

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
let initialized = false;

/**
 * Inject the fbevents.js script and fire the initial PageView.
 * Safe to call multiple times — only the first call has any effect.
 */
export function initMetaPixel(): void {
    if (initialized) return;
    if (!PIXEL_ID) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (window.fbq) {
        initialized = true;
        return;
    }

    // Standard Meta Pixel base snippet, adapted for TS. Sets up the queueing
    // shim, then async-loads fbevents.js, then fires init + PageView.
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const n: any = function (...args: unknown[]) {
        if (n.callMethod) n.callMethod.apply(n, args);
        else n.queue.push(args);
    };
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    window.fbq = n as FbqFn;
    if (!window._fbq) window._fbq = n;
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const scriptEl = document.createElement("script");
    scriptEl.async = true;
    scriptEl.src = "https://connect.facebook.net/en_US/fbevents.js";
    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(scriptEl, firstScript);
    } else {
        document.head.appendChild(scriptEl);
    }

    n("init", PIXEL_ID);
    n("track", "PageView");
    initialized = true;
}

/**
 * Fire a Meta Pixel standard or custom event. No-ops if Pixel isn't loaded.
 *
 * Standard events: PageView, Lead, CompleteRegistration, Purchase, etc.
 * See https://developers.facebook.com/docs/meta-pixel/reference
 */
export function trackPixelEvent(event: string, data?: Record<string, unknown>): void {
    if (typeof window === "undefined") return;
    if (typeof window.fbq !== "function") return;
    window.fbq("track", event, data);
}
